import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import imageCompression from "browser-image-compression";
import { Input } from "@/shared/components/ui/input";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import t from "@/lang/lang";
import { AlertCircle, ImageUp, Upload } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

type ImageUploadProps = {
  existingImageUrl?: string;
  wrapperClassName?: string;
  imageClassName?: string;
  showImagePreview?: boolean;
  id?: string;
  onUpload?: (file: File) => void;
  err?: { title: string; description: string } | null;
  disabled?: boolean;
  actions?: ReactNode;
};

export function ImageUpload({
  existingImageUrl,
  wrapperClassName,
  imageClassName,
  showImagePreview = true,
  id,
  onUpload,
  err,
  disabled,
  actions,
}: ImageUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<{
    title: string;
    description: string;
  } | null>(err || null);

  useEffect(() => {
    if (!selectedFile) {
      return () => {};
    }

    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [selectedFile]);

  const convertToPng = (file: File): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result !== "string") {
          reject(new Error("Invalid file reader result"));
          return;
        }

        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Canvas not supported"));
            return;
          }

          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Failed to convert to PNG"));
              }
            },
            "image/png",
            1,
          );
        };

        img.onerror = () => reject(new Error("Image failed to load"));
        img.src = reader.result;
      };

      reader.onerror = () => reject(new Error("File reading failed"));
      reader.readAsDataURL(file);
    });

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (error) {
      setError(null);
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setError({
        title: t("Fehler"),
        description: t("Es sind nur PNG- oder JPEG-Bilder zulässig."),
      });
      return;
    }

    if (file.size <= 30 * 1024) {
      if (file.type === "image/jpeg") {
        const pngBlob = await convertToPng(file);
        const pngFile = new File(
          [pngBlob],
          file.name.replace(/\.(jpg|jpeg)$/i, ".png"),
          { type: "image/png" },
        );
        setSelectedFile(pngFile);
      } else {
        setSelectedFile(file);
      }
      return;
    }

    const options = {
      maxSizeMB: 35 / 1024,
      maxWidthOrHeight: 250,
      useWebWorker: true,
      fileType: "image/png",
    };

    try {
      const compressedFile = await imageCompression(file, options);

      if (compressedFile.size > 35 * 1024) {
        setError({
          title: t("Fehler"),
          description: `${t("Das Bild ist nach der Komprimierung immer noch größer als 35 KB.")}: ${(compressedFile.size / 1024).toFixed(2)}KB`,
        });
        return;
      }

      setSelectedFile(compressedFile);
    } catch (er) {
      setError({
        title: t("Fehler"),
        description: `${t("Die Bildkomprimierung ist fehlgeschlagen")}: ${er}`,
      });
    }
  };

  const uploadImage = () => {
    if (onUpload && selectedFile) {
      onUpload(selectedFile);
    }
  };

  const imageToDisplay = previewUrl || existingImageUrl;

  return (
    <div
      className={`flex h-full flex-col justify-between gap-2 ${wrapperClassName || ""}`}
    >
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{error.title}</AlertTitle>
          <AlertDescription>{error.description}</AlertDescription>
        </Alert>
      )}
      <div className="flex h-full items-center justify-center">
        {showImagePreview && imageToDisplay && !error && (
          <img
            src={imageToDisplay}
            alt="Preview"
            className={`max-w-[450px] overflow-clip rounded ${imageClassName || ""}`}
          />
        )}
        {showImagePreview && !imageToDisplay && !error && (
          <ImageUp
            strokeWidth="1.2px"
            size="200px"
            className="text-secondary"
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <Input
          id={id}
          type="file"
          accept="image/png,image/jpeg"
          className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
          onChange={handleImageChange}
          disabled={disabled}
        />
        <TooltipProvider>
          {actions}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="default"
                onClick={uploadImage}
                disabled={!selectedFile || disabled}
                className="cursor-pointer"
              >
                <Upload className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("Bild hochladen")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
